import React from "react";
import { useTranslation } from "react-i18next";
import {
    StyledModal,
    StyledH2,
    StyledLabel,
    CheckboxIcon,
} from "@aureskonnect/react-ui";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import { useSnapshot } from "valtio";

import { store } from "@components/VerticalLayout/store";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { useFetchGlobal } from "../../../../api/hooks";

import { dissociateApplicationPeripherals } from "./api";
import {
    collectPeripheralRows,
    groupPeripheralsByApplication,
} from "./helpers";

type ConfirmationDissociateType = {
    isModalOpened: boolean;
    setIsModalOpened: (value: boolean) => void;
    selectedRows: any[];
    setDataIsUpdated: (value: boolean | number) => void;
};

export function ConfirmationDissociate({
    isModalOpened,
    setIsModalOpened,
    selectedRows,
    setDataIsUpdated,
}: ConfirmationDissociateType) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const { userID, franchiseID, shopID, operatorID, oneShop } = useSnapshot(
        store
    );

    async function handleDissociatePeripherals() {
        const peripheralRows = collectPeripheralRows(selectedRows);

        if (peripheralRows.length === 0) return;

        // One grouped request per application instead of one DELETE per
        // peripheral.
        const appGroups = groupPeripheralsByApplication(peripheralRows);

        try {
            const results = await Promise.all(
                appGroups.map((group) =>
                    dissociateApplicationPeripherals(fetchGlobal, {
                        userID: String(userID),
                        franchiseID,
                        operatorID,
                        oneShop,
                        shopID: shopID.toString(),
                        appId: group.appId,
                        designation: group.designation,
                        appShopId: group.shopId,
                        peripherals: group.items.map((row: any) => ({
                            peripheralId: row.peripheralId,
                            peripheralType: row.peripheralType,
                            name: row[t("Peripheral")],
                        })),
                    })
                )
            );

            const hasError = results.some((r: any) => r && r.error);
            if (hasError) throw Error("error");

            toast.success(
                `${t("The dissociation was successfully completed.")}`,
                {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000,
                    theme: "colored",
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    icon: <CheckboxIcon height={25} width={25} fill="white" />,
                }
            );
            setDataIsUpdated(true);
        } catch (e) {
            toast.error(`${t("There's an error")}`, {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    }

    return (
        <StyledModal toggle={true} isOpen={isModalOpened} centered>
            <ModalHeader
                className="text-capitalize"
                toggle={() => setIsModalOpened(!isModalOpened)}
            >
                <StyledH2 className="text-uppercase">{t("Alert")}</StyledH2>
            </ModalHeader>
            <ModalBody>
                <StyledLabel className="pl-3 m-0">
                    {t("Please confirm unpairing of the selected devices.")}
                </StyledLabel>
            </ModalBody>
            <ModalFooter className="border-top-0">
                <CustomSecondaryColorButton
                    outline
                    variant="light"
                    className="afa_btn_cancelAlert"
                    onClick={() => setIsModalOpened(!isModalOpened)}
                    rounded
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    rounded
                    className="mr-2 afa_btn_confirmAlert"
                    variant="primary"
                    onClick={async () => {
                        setIsModalOpened(!isModalOpened);
                        await handleDissociatePeripherals();
                    }}
                >
                    {t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
        </StyledModal>
    );
}
